/**
 * Helpers: Product accordion
 * ------------------------------------------------------------------------------
 * Helper functions for building an accordion from product data.
 */
import { decodeHTMLEntities, stripBeforeFirstUl } from '~/helpers/strings'

/**
 * Generate an empty placeholder array of accordion content.
 * @param {String} deliveryReturnsTitle - Delivery & Returns title
 * @returns {Array}
 */
export function getAccordionsTemplate(deliveryReturnsTitle = '') {
  return [
    {
      id: 'productDetailsFit',
      label: mint.Strings('product_detail_page.accordion.product_details_and_fit'),
      value: '',
    },
    {
      id: 'fabricCare',
      label: mint.Strings('product_detail_page.accordion.fabric_and_care'),
      value: '',
    },
    {
      id: 'deliveryReturns',
      label: deliveryReturnsTitle || 'Delivery &amp; Returns',
      value: '',
    },
  ]
}

/**
 * Generate accordion data.
 * @param {Object} product - Product data object
 * @param {Object} deliveryReturns - Delivery & Returns title and body content
 * @param {Object} [uspList] - USP list data
 * @returns {Array}
 */
export function buildProductAccordion(product = {}, deliveryReturns = {}, uspList = {}) {
  return [
    {
      id: 'productDetailsFit',
      label: mint.Strings('product_detail_page.accordion.product_details_and_fit'),
      value: getProductDetailsFit(product),
    },
    {
      id: 'fabricCare',
      label: mint.Strings('product_detail_page.accordion.fabric_and_care'),
      value: getFabricCare(product),
    },
    {
      id: 'deliveryReturns',
      label: deliveryReturns.title || 'Delivery &amp; Returns',

      value: (!uspList || !uspList?.items) ?
        deliveryReturns.body : true,
    },
  ]
}

/**
 * Generate "Details & Fit" content from Patchworks or legacy Fusefabric fields
 * @param {Object} product
 * @returns {String}
 */
function getProductDetailsFit(product) {
  if (!product?.whyWeLoveIt && !product?.fusefabric?.whyWeLoveIt) {
    return ''
  }

  if (!product.isPatchworks) {
    return getLegacyProductDetailsFit(product)
  }

  const fields = [
    product.whyWeLoveIt,
    product.sizeFit,
  ]

  return generatePatchworksContent(fields)
}

/**
 * Generate "Fabric & Care" content from Patchworks or legacy Fusefabric fields
 * @param {Object} product
 * @returns {String}
 */
function getFabricCare(product) {
  if (!product.isPatchworks) {
    return getLegacyFabricCare(product)
  }

  let generatedContent = ''

  const fields = [
    product.detailsCare,
    product.fabricComp,
    product.washCare,
    product.styleId,
    product.countryOfOrigin,
  ]

  /**
   * Field labels
   * - These are aligned with the product field values above
   * - The "detailsCare" field should never have a label, so the value is set
   *   to false
   */
  const fieldLabels = [
    false,
    mint.Strings('product_detail_page.accordion.fabric_comp'),
    mint.Strings('product_detail_page.accordion.wash_care'),
    mint.Strings('product_detail_page.accordion.style_id'),
    mint.Strings('product_detail_page.accordion.country_of_origin'),
  ]

  generatedContent = generatePatchworksContent(fields, fieldLabels, product)

  // Append `Safety Instructions` metafield output
  if (product?.safetyInstructions) {
    const safetyInstructionsHtml = `
      <h4 class="product-description__safety-instructions-title">
        ${mint.Strings('product_detail_page.accordion.safety_instructions')}
      </h4>
      <div class="product-description__safety-instructions-content">
        ${generatePatchworksContent([product.safetyInstructions], [], product)}
      </div>
    `

    generatedContent += safetyInstructionsHtml
  }

  return generatedContent
}

/**
 * Generate a stringified list element containing an array of metafields.
 * - List elements will be deconstructed to group all content into one list.
 * @param {Array} fields
 * @param {Array} labels
 * @param {Object} product - Product data object
 * @returns {String}
 */
function generatePatchworksContent(fields = [], labels = [], product = false) {
  const list = document.createElement('ul')

  const items = fields.map((field, index) => {

    /**
     * Return early if a field is empty.
     */
    if (!field) {
      return false
    }

    /**
     * Check for opening or closing `ul` tags
     */
    const isList = /<(\/?|!?)(ul)>/g.test(field)

    /**
     * Return plaintext fields early
     */
    if (!isList) {
      if (labels.length && labels[index]) {
        return `<li><strong>${labels[index]}</strong> ${field}</li>`
      }

      return `<li>${field}</li>`
    }

    /**
     * Get the inner contents of a <ul>...</ul> string
     * - String replacement has not been used as it would not handle tags with
     *   attributes, e.g. <ul style="...">
     * - Create a temporary wrapper document containing the `ul` element
     * - Trim and return the inner content of the `ul` element
     */
    const listWrapper = new DOMParser().parseFromString(field, 'text/html')

    return listWrapper
      .querySelector('ul')
      ?.innerHTML
      ?.trim()
      || false
  }).filter(Boolean)

  list.innerHTML = items.join('')

  /**
   * Strip any style attributes from the `li` elements
   */
  list.childNodes.forEach(listItem => {
    listItem.removeAttribute('style')
  })

  return list.outerHTML
}

/**
 * Returns Product Details and Fit content, optionally with Why We Love It content as the first item.
 * If whyWeLoveIt is not blank create a new list item with whyWeLoveIt as its content.
 * If sizeFit is blank create a new unordered list with the new list item as its only child.
 * If sizeFit is not blank check to see if it's a plain text string or unordered list.
 * - if it's a list insert newItem (whyWeLoveIt) as the first item in the sizeFit unordered list.
 * - if it's a string create unordered list for newItem and sizeFit list items.
 * If whyWeLoveIt is blank return the original sizeFit string.
 * @returns {String}
 */
function getLegacyProductDetailsFit(product = {}) {
  const whyWeLoveIt = product?.fusefabric?.whyWeLoveIt || ''
  const sizeFit = decodeHTMLEntities(product?.fusefabric?.sizeFit) || ''

  // Check if whyWeLoveIt is not blank
  if (whyWeLoveIt) {
    const newItem = `<li>${whyWeLoveIt}</li>` // Create a new list item

    if (!sizeFit) {
      return `<ul>${newItem}</ul>` // If sizeFit is blank, create a new <ul> with newItem as the first list item
    }

    // Check if sizeFit is a list or a plain string
    if (sizeFit.startsWith('<ul>')) {
      const mergedSizeFit = sizeFit.replace('<ul>', `<ul>${newItem}`) // If sizeFit is a list, insert newItem as the first child of the sizeFit <ul>
      return mergedSizeFit
    }
    else {
      return `<ul>${newItem}<li>${sizeFit}</li></ul>` // If sizeFit is a plain string, create a new <ul> with newItem and sizeFit as list items
    }
  }

  return sizeFit // If whyWeLoveIt is blank, return the original sizeFit string
}

/**
 * Returns the HTML code for the fabric care accordion panel content
 * @returns {String}
 */
function getLegacyFabricCare(product = {}) {
  // Get the fabric care details from the product data, or use an empty string as a default
  const fabricCare = product?.fusefabric?.detailsCare || ''

  // Use getFirstList() to extract the first unordered list from the detailsCare string and assign the returned values to the corresponding variables
  const [firstListHtml, items] = getFirstList(fabricCare)

  if (!items) {
    return ''
  }

  // Map over each item, assign different title based on the index
  const listItems = items.map((item, index) => {
    // Skip this item if it's an empty string
    if (item === '') {
      return null
    }

    let title = ''

    switch (index) {
      case 0:
        title = 'Composition:'
        break
      case 1:
        title = 'Garment Care:'
        break
      case 2:
        title = 'Style-id:'
        break
      default:
        title = ''
    }

    const newTitle = title.replace('Style-id:', 'Style ID:')

    // List string items in the Fabric & Care content that need replacing, note these correspond with the list in accordion.js
    const bodyReplaceObj = {
      'Main,': 'Main:',
      'Style-id:': '',
      ',lining,': '; Lining:',
      ',coating,': '; Coating:',
      ',sole,': '; Sole:',
      ',sock,': '; Sock:',
      ',lining 2,': '; Lining 2:',
      ',fabric,': '; Fabric:',
      'style=': 'data-style=',
    }

    // Replace multiple attributes in the item string with corresponding values from the bodyReplaceObj object
    const newItem = replaceAttributes(item, bodyReplaceObj)

    // Return a <li> tag with the new title and the item
    return `<li><strong>${newTitle}</strong> ${newItem}</li>` // Use the new title instead of the original title along with the newItem
  })

  /**
   * Remove any inline styles that could appear in either list.
   */
  const newStyleFirst = firstListHtml ? replaceAttributes(firstListHtml, {
    'style=': 'data-style=',
  }) : ''

  let result = newStyleFirst

  // Append `Country of origin` list item
  if (product?.countryOfOrigin) {
    listItems.push(
      `<li>
        <strong>Country of origin:</strong> ${product?.countryOfOrigin}
      </li>`,
    )
  }

  // Only create and append secondListHtml if listItems array is not empty
  if (listItems.length > 0) {
    const secondListHtml = `<ul>${listItems.join('\n')}</ul>` // Create a new <ul> element with the list as its content
    result += secondListHtml // Merge the first and second unordered lists
  }

  return result
}

/**
 * Helper function for fabricCare - replaces multiple attributes in the item string with corresponding values
 * from the itemObj object using a regular expression
 * @param {String} item The string that contains the item information
 * @param {Object} itemObj The object that maps the attributes to be replaced with their replacement values
 * @returns {String} The new item string with the replaced attributes
 */
function replaceAttributes(item, itemObj) {

  // Create a regular expression from the keys of the itemObj object and join them with the or operator
  const regex = new RegExp(Object.keys(itemObj).join('|'), 'g')

  // Replace each matched attribute in the item string with the corresponding value from the itemObj object
  const newItem = item.replaceAll(regex, matched => itemObj[matched])

  return newItem
}

/**
 * Extract the first unordered list and the remaining text without the html code from the detailsCare string, whether it is encoded or not
 * @param {String}
 * @returns {Array}
 */
function getFirstList(detailsCare = '') {
  // Helper function for fabricCare, accounts for content as encoded html or ~ delimited
  // If detailsCare is an empty string, return null for firstListHtml and an empty array for items
  if (!detailsCare.length) {
    return [null, []]
  }

  // Strip everything before the first <ul>
  detailsCare = stripBeforeFirstUl(decodeHTMLEntities(detailsCare))

  // Use a regular expression to match the first <ul> element in the string
  const regex = /<ul>.*<\/ul>|&lt;ul&gt;.*&lt;\/ul&gt;/
  const match = detailsCare.match(regex) // get the html code as a string and assign it to a variable

  if (!match) {
    // If no <ul> found, split the string by '~' and filter out empty strings
    // Return null for firstListHtml and the split items as an array
    const items = detailsCare.split('~').filter(item => item !== '')
    return [null, items]
  }

  const [firstListHtml] = match
  const text = detailsCare.replace(regex, '') // Remove the html code from the detailsCare string and assign it to another variable

  // Split the remaining text by '~' and filter out empty strings
  const items = text.split('~').filter(item => item !== '')

  // Return the firstListHtml and the split items as an array
  return [firstListHtml, items] // Return both variables as an array
}
